import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounce } from '../lib/debounce';
import { getHue } from '../lib/get-hue';
import { objectsMatch } from '../lib/objects-match';
import { paddTo } from '../lib/padd-to';
import { hslToRgb, parseColor, rgbToHsl } from '../lib/parse-color';
import { sleep } from '../lib/sleep';
import { InputFieldComponent } from '../shared-extra/components/input-field/input-field.component';
import { InputPostDirective } from '../shared-extra/directives/input-post.directive';
import { InputPreDirective } from '../shared-extra/directives/input-pre.directive';
import { SvgComponent } from '../shared/components/svg/svg.component';
import { ClickTrackDirective } from '../shared/directives/click-track.directive';
import { IsDarkColorPipe } from './is-dark-color.pipe';

declare global {
  interface Window {
    EyeDropper: {
      new (): {
        open: () => Promise<{ sRGBHex: string }>;
      };
    };
  }
}

@Component({
  selector: 'app-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgComponent,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    InputPreDirective,
    InputPostDirective,
    IsDarkColorPipe,
    ClickTrackDirective,
  ],
})
export class ColorPickerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() color: string;
  @Input() allowOpacity = false;
  @Output() colorChange = new EventEmitter<string>();
  @ViewChild('gradient') gradient: ElementRef<HTMLCanvasElement>;
  @ViewChild('hueEl') hueEl: ElementRef<HTMLDivElement>;
  @ViewChild('opacityEl') opacityEl: ElementRef<HTMLDivElement>;
  ctx: CanvasRenderingContext2D;
  hue = 0;
  ourSetColor: string;
  gradientPosition = { x: 0, y: 0 };
  rgba: { r: number; g: number; b: number; a: number };
  mouseDownGradient = false;
  mouseDownHue = false;
  mouseDownOpacity = false;
  allowEyeDropper = !!window.EyeDropper;
  form = new FormGroup({
    hex: new FormControl(''),
    opacity: new FormControl(null),
  });
  formHexSub: Subscription;
  formOpacitySub: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.color) return;
    const color = changes.color.currentValue;

    this.rgba = parseColor(color) || { r: 0, g: 0, b: 0, a: this.allowOpacity ? 0 : 1 };

    const opacity = Math.round(this.rgba.a * 100); // 0-100 with no decimal places

    if (this.form.value.opacity === null) {
      this.form.controls.opacity.setValue(opacity);
    } else if (this.form.value.opacity !== opacity) {
      this.rgba.a = this.form.value.opacity / 100;
      this.setColor(this.getHex(this.rgba.r, this.rgba.g, this.rgba.b, this.rgba.a));
    }

    const hex = this.getHex(this.rgba.r, this.rgba.g, this.rgba.b).substring(1);
    if (this.form.value.hex !== hex) {
      this.form.controls.hex.setValue(hex);
    }

    if (this.color === this.ourSetColor) return;

    this.hue = this.rgba ? getHue(this.rgba.r, this.rgba.g, this.rgba.b) : 0;
    this.drawGradient();
    this.calculateGradientPosition();
  }

  async ngAfterViewInit() {
    await sleep(0);
    this.ctx = this.gradient.nativeElement.getContext('2d', { willReadFrequently: true });
    this.drawGradient();
    this.calculateGradientPosition();

    this.formHexSub = this.form.controls.hex.valueChanges.subscribe(hex => {
      if (hex.includes('#')) this.form.controls.hex.setValue(hex.replace('#', ''));
      hex = '#' + hex.toUpperCase().replace('#', '');
      if (hex.length !== 7) return;

      const rgba = parseColor(hex);
      rgba.a = this.rgba.a;
      const hexValid = Object.values(rgba).every(v => !isNaN(v));

      if (hexValid) {
        if (objectsMatch(this.rgba, rgba)) return;
        this.setColorAndAuto(hex);
      }
    });

    this.formOpacitySub = this.form.controls.opacity.valueChanges.subscribe(opacity => {
      this.rgba.a = parseFloat(opacity) / 100;
      this.setColor(this.getHex(this.rgba.r, this.rgba.g, this.rgba.b, this.rgba.a));
    });
  }

  pickColorFromGradient(event: MouseEvent) {
    if (event.buttons !== 1) return;
    event.preventDefault();
    const canvas = this.gradient.nativeElement;

    const { top, left, width, height } = canvas.getBoundingClientRect();
    const x = Math.max(Math.min(event.clientX - left, width), 0) / width;
    const y = Math.max(Math.min(event.clientY - top, height), 0) / height;
    const { data } = this.ctx.getImageData(x * (canvas.width - 1), y * canvas.height, 1, 1);

    const rgb = { r: data[0], g: data[1], b: data[2] };
    this.gradientPosition = { x, y };
    this.setColor(this.getHex(rgb.r, rgb.g, rgb.b));
  }

  setColor(hex: string) {
    if (hex.length === 7) {
      hex += paddTo(Math.round(this.rgba.a * 255).toString(16), 2);
    }
    this.ourSetColor = hex.toUpperCase();
    this.emitNewColor(hex.toUpperCase());
  }

  setColorAndAuto(hex: string) {
    if (hex.length === 7) {
      hex += paddTo(Math.round(this.rgba.a * 255).toString(16), 2);
    }
    this.emitNewColor(hex.toUpperCase());
  }

  emitNewColor = debounce((hex: string) => {
    this.color = hex;
    this.colorChange.emit(hex.toUpperCase());
  }, 0);

  pointerEnterGradient(event: PointerEvent) {
    if (event.buttons !== 1) return;
    this.mouseDownGradient = true;
  }

  pointerEnterHue(event: PointerEvent) {
    if (event.buttons !== 1) return;
    this.mouseDownHue = true;
  }

  pointerEnterOpacity(event: PointerEvent) {
    if (event.buttons !== 1) return;
    this.mouseDownOpacity = true;
  }

  async startEyeDropper() {
    if (!this.allowEyeDropper) return;
    const eyeDropper = new window.EyeDropper();
    const resp = await eyeDropper.open();
    this.setColorAndAuto(resp.sRGBHex);
  }

  pickHueFromSelector(event: PointerEvent) {
    if (event.buttons !== 1) return;
    event.preventDefault();
    const { left, width } = this.hueEl.nativeElement.getBoundingClientRect();
    const x = Math.max(Math.min(event.clientX - left, width), 0) / width;
    this.hue = Math.round(x * 360);
    const hsl = rgbToHsl(this.rgba.r, this.rgba.g, this.rgba.b);
    hsl.h = this.hue / 360;
    const rgb = hslToRgb(hsl.h, hsl.s, hsl.l);
    const hexString = this.getHex(rgb.r, rgb.g, rgb.b);
    this.setColor(hexString);
    this.drawGradient();
  }

  pickOpacityFromSelector(event: PointerEvent) {
    if (event.buttons !== 1) return;
    const { left, width } = this.opacityEl.nativeElement.getBoundingClientRect();
    const x = Math.max(Math.min(event.clientX - left, width), 0) / width;
    this.form.controls.opacity.setValue(Math.round(x * 100));
  }

  calculateGradientPosition() {
    if (!this.gradient) return;
    const { r, g, b } = this.rgba;

    const data = this.ctx.getImageData(0, 0, this.ctx.canvas.width, this.ctx.canvas.height).data;

    for (let i = 0; i < data.length; i += 4) {
      const _r = data[i];
      const _g = data[i + 1];
      const _b = data[i + 2];

      const dR = Math.abs(_r - r);
      const dG = Math.abs(_g - g);
      const dB = Math.abs(_b - b);

      const threshhold = 2;

      if (dR < threshhold && dG < threshhold && dB < threshhold) {
        const x = (i / 4) % this.ctx.canvas.width;
        const y = Math.floor(i / 4 / this.ctx.canvas.width);

        this.gradientPosition = { x: x / this.ctx.canvas.width, y: y / this.ctx.canvas.height };
        return;
      }
    }
    console.warn('Could not find color in gradient');
  }

  drawGradient() {
    if (!this.gradient) return;

    const color = `hsl(${this.hue}, 100%, 50%)`;

    // Horizontal color gradient
    const gradientH = this.ctx.createLinearGradient(0, 0, this.ctx.canvas.width, 0);
    gradientH.addColorStop(0, '#fff');
    gradientH.addColorStop(1, color);
    this.ctx.fillStyle = gradientH;
    this.ctx.fillRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);

    // Vertical black and white gradient
    const gradientV = this.ctx.createLinearGradient(0, 0, 0, this.ctx.canvas.height);
    gradientV.addColorStop(0, 'rgba(0,0,0,0)');
    gradientV.addColorStop(1, 'rgba(0,0,0,1)');
    this.ctx.fillStyle = gradientV;
    this.ctx.fillRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
    this.ctx.fillStyle = '#fff';
    this.ctx.fillRect(0, 0, 1, 1); // making sure that the top left is white
  }

  getHex(r: number, g: number, b: number, a?: number) {
    if (a) {
      return `#${paddTo(r.toString(16), 2)}${paddTo(g.toString(16), 2)}${paddTo(b.toString(16), 2)}${paddTo(
        Math.round(a * 255).toString(16),
        2
      )}`.toUpperCase();
    }
    return `#${paddTo(r.toString(16), 2)}${paddTo(g.toString(16), 2)}${paddTo(b.toString(16), 2)}`.toUpperCase();
  }

  ngOnDestroy(): void {
    this.formHexSub?.unsubscribe();
    this.formOpacitySub?.unsubscribe();
  }
}
